import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ThemeService } from '../../shared-services/theme.service';

@Component({
  selector: 'app-comparison-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './comparison-bar-chart.component.html',
  styleUrls: ['./comparison-bar-chart.component.scss']
})
export class ComparisonBarChartComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private themeService = inject(ThemeService);

  private _data: any;
  labels: string[] = [];
  values: number[] = [];

  comparisons: Array<number | null> = [];
  overallChange = 0;
  maxValue = 0;
  readonly maxBarHeight = 120;
  private readonly labelZoneHeight = 30;
  private readonly valueZoneHeight = 20;
  private readonly connectorGap = 46;
  private readonly tipValueClearance = 12;
  private readonly firstBarLevelStep = 16;

  protected get comparisonType(): 'first-bar' | 'adjacent' {
    const mode = `${this.data?.comparisonType || 'first-bar'}`.toLowerCase();
    return mode === 'adjacent' ? 'adjacent' : 'first-bar';
  }

  protected get connectorEnabled(): boolean {
    const raw = this.data?.showConnector ?? this.data?.connectorEnabled;
    if (raw === undefined || raw === null) {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  shouldShowConnector(): boolean {
    return this.connectorEnabled && this.values.length > 1;
  }

  protected get comparisonBubblesEnabled(): boolean {
    if (this.data?.chartConfig?.showLabels === false || this.data?.chartConfig?.showDataLabels === false) {
      return false;
    }

    const raw = this.data?.showComparisonBubbles;
    if (raw === undefined || raw === null) {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  shouldShowComparisonBubbles(): boolean {
    return this.comparisonBubblesEnabled && this.values.length > 1;
  }

  shouldShowValues(): boolean {
    if (this.data?.chartConfig?.showDataLabels === false) {
      return false;
    }

    const raw = this.data?.showValues;
    if (raw === undefined || raw === null) {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  shouldShowLabels(): boolean {
    if (this.data?.chartConfig?.showLabels === false) {
      return false;
    }

    const raw = this.data?.showLabels;
    if (raw === undefined || raw === null) {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  getBottomLabel(): string {
    if (!this.shouldShowLabels()) {
      return '';
    }

    return `${this.data?.bottomLabel ?? this.data?.xAxisLabel ?? this.data?.axisLabel ?? ''}`.trim();
  }

  isFirstBarMode(): boolean {
    return this.comparisonType === 'first-bar';
  }

  set data(value: any) {
    this._data = value;
    this.onDataAssigned();
  }

  get data(): any {
    return this._data;
  }

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService
  ) {
    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (Object.keys(filters || {}).length !== 0) {
          this.loadChartData(filters);
        }
      });
  }

  protected onDataAssigned(): void {
    if (!this.data) {
      return;
    }

    if (this.data.apiUrl) {
      this.loadChartData();
      return;
    }

    this.bindDirectData(this.data);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected loadChartData(filters?: any): void {
    this.configService.getConfig(
      this.data.apiUrl,
      this.data,
      { ...filters, useQueryParams: this.data?.useQueryParams || false },
      this.data.method
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const parsed = this.extractChartData(res);
        this.bindDirectData(parsed);
      },
      error: () => {
        this.labels = [];
        this.values = [];
        this.comparisons = [];
        this.overallChange = 0;
        this.maxValue = 0;
      }
    });
  }

  protected extractChartData(response: any): { labels: string[]; values: number[]; comparisons?: Array<number | null>; overallChange?: number } {
    const responsePath = this.data?.responsePath;
    const comparisonResponsePath = this.data?.comparisonResponsePath;
    const dataKey = this.data?.dataKey || 'groupData';
    const labelKey = this.data?.labelKey;
    const valueKey = this.data?.valueKey || 'count';
    const percentageKey = this.data?.percentageKey || 'percentage';
    const overallChangeKey = this.data?.overallChangeKey;
    const sortLabels = this.data?.sortLabels !== false;

    const baseData = responsePath
      ? this.getByPath(response, responsePath)
      : this.resolveBaseData(response, dataKey);
    const comparisonBaseData = comparisonResponsePath
      ? this.getByPath(response, comparisonResponsePath)
      : undefined;

    const groupData = baseData?.[dataKey] ?? baseData;

    if (Array.isArray(groupData)) {
      const labels = groupData.map((item: any, idx: number) => `${item?.[labelKey] ?? item?.label ?? idx + 1}`);
      const values = groupData.map((item: any) => Number(item?.[valueKey] ?? item?.value ?? 0));
      const comparisons = this.extractArrayComparisons(groupData, comparisonBaseData, percentageKey, labels.length);

      return {
        labels,
        values,
        comparisons,
        overallChange: Number(baseData?.[overallChangeKey])
      };
    }

    if (groupData && typeof groupData === 'object') {
      let orderedKeys = Object.keys(groupData);
      if (sortLabels) {
        orderedKeys = orderedKeys.sort((a, b) => Number(a) - Number(b));
      }

      const labels = orderedKeys.map((key) => {
        const record = groupData[key];
        return `${record?.[labelKey] ?? key}`;
      });

      const values = orderedKeys.map((key) => {
        const record = groupData[key];
        if (record !== null && typeof record === 'object' && !Array.isArray(record)) {
          const value = Number(record?.[valueKey] ?? record?.value ?? 0);
          return Number.isFinite(value) ? value : 0;
        }

        const value = Number(record);
        return Number.isFinite(value) ? value : 0;
      });

      const comparisons = this.extractObjectComparisons(groupData, comparisonBaseData, orderedKeys, percentageKey);

      return {
        labels,
        values,
        comparisons,
        overallChange: Number(baseData?.[overallChangeKey])
      };
    }

    if (Array.isArray(baseData?.category) && Array.isArray(baseData?.value)) {
      return {
        labels: baseData.category,
        values: baseData.value.map((v: any) => Number(v ?? 0)),
        comparisons: this.data?.comparisons,
        overallChange: Number(baseData?.[overallChangeKey] ?? this.data?.overallChange)
      };
    }

    return {
      labels: this.data?.labels || [],
      values: this.data?.values || [],
      comparisons: this.data?.comparisons,
      overallChange: this.data?.overallChange
    };
  }

  protected resolveBaseData(response: any, dataKey: string): any {
    if (!response || typeof response !== 'object') {
      return response;
    }

    const candidatePaths = [
      '',
      'data',
      'data.0',
      'responseData',
      'responseData.0',
      'responseList.0',
      'responseList.0.data',
      'responseList.0.data.0',
      'responseList.0.responseData',
      'responseList.0.responseData.0',
      'ui-data.0.data'
    ];

    for (const path of candidatePaths) {
      const candidate = path ? this.getByPath(response, path) : response;
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      if (candidate[dataKey] !== undefined) {
        return candidate;
      }

      if (Array.isArray(candidate.category) && Array.isArray(candidate.value)) {
        return candidate;
      }
    }

    return response;
  }

  protected bindDirectData(source: any): void {
    this.labels = source?.labels || [];
    this.values = source?.values || [];

    if (this.values.length > 1) {
      this.prepareChart(source);
    } else {
      this.maxValue = this.values.length ? Math.max(...this.values) : 0;
      this.comparisons = [];
      this.overallChange = 0;
    }
  }

  prepareChart(source?: any): void {
    const values = this.values || [];

    if (!values.length) {
      this.maxValue = 0;
      this.comparisons = [];
      this.overallChange = 0;
      return;
    }

    this.maxValue = Math.max(...values);

    const providedComparisons = this.normalizeComparisons(source?.comparisons, values.length);
    if (providedComparisons) {
      this.comparisons = providedComparisons;
    } else {
      this.comparisons = [];
      for (let i = 0; i < values.length; i++) {
        if (i === 0) {
          this.comparisons.push(null);
          continue;
        }

        const baseline = this.comparisonType === 'adjacent'
          ? values[i - 1]
          : values[0];

        const percent = baseline === 0 ? 0 : ((values[i] - baseline) / baseline) * 100;
        this.comparisons.push(Math.round(percent));
      }
    }

    if (typeof source?.overallChange === 'number' && Number.isFinite(source.overallChange)) {
      this.overallChange = Math.round(source.overallChange);
    } else {
      const first = values[0];
      const last = values[values.length - 1];
      const percent = first === 0 ? 0 : ((last - first) / first) * 100;
      this.overallChange = Math.round(percent);
    }
  }

  getBarHeight(value: number): number {
    if (!this.maxValue) {
      return 0;
    }

    return Math.max(8, (value / this.maxValue) * this.maxBarHeight);
  }

  formatPercent(value: number, includePlus = true): string {
    if (value > 0 && includePlus) {
      return `+${value}%`;
    }

    return `${value}%`;
  }

  hasComparison(index: number): boolean {
    const value = this.comparisons[index];
    return typeof value === 'number' && Number.isFinite(value);
  }

  getBarComparison(index: number): number | null {
    const value = this.comparisons[index];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  getBarColor(index: number): string {
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const configuredPalette = Array.isArray(this.data?.barColors) ? this.data.barColors : [];
    const fallbackPalette = theme.chart?.palette || ['#162c62', '#3f3f3f', '#1f67b8', '#f2bc00', '#df2a21', '#6d6d6d'];
    const palette = configuredPalette.length ? configuredPalette : fallbackPalette;
    return palette[index % palette.length];
  }

  getConnectorBottom(): number {
    if (!this.values.length) {
      return this.labelZoneHeight + this.valueZoneHeight + this.connectorGap;
    }

    const tallestBar = Math.max(...this.values.map((value) => this.getBarHeight(value)));
    return this.labelZoneHeight + this.valueZoneHeight + tallestBar + this.connectorGap;
  }

  getBarTopBottom(index: number): number {
    if (index < 0 || index >= this.values.length) {
      return this.labelZoneHeight + this.valueZoneHeight;
    }

    return this.labelZoneHeight + this.valueZoneHeight + this.getBarHeight(this.values[index]);
  }

  getPairTopBottom(index: number): number {
    if (index <= 0 || index >= this.values.length) {
      return this.labelZoneHeight + this.valueZoneHeight;
    }

    return Math.max(this.getBarTopBottom(index - 1), this.getBarTopBottom(index));
  }

  getTipBottom(index: number): number {
    return this.getBarTopBottom(index) + this.tipValueClearance;
  }

  getTipHeight(index: number): number {
    return Math.max(8, this.getConnectorBottom() - this.getTipBottom(index));
  }

  getTopPadding(): number {
    if (!this.shouldShowConnector()) {
      return 56;
    }

    if (!this.isFirstBarMode() || this.values.length <= 2) {
      return 56;
    }

    return 56 + ((this.values.length - 2) * this.firstBarLevelStep);
  }

  getFirstBarBranchBottom(index: number): number {
    const levelFromClosest = Math.max(0, index - 1);
    return this.getConnectorBottom() + (levelFromClosest * this.firstBarLevelStep);
  }

  getFirstBarBranchWidth(index: number): string {
    return `calc(${index} * (var(--bar-width) + var(--bar-gap, 0px)))`;
  }

  getFirstBarTrunkHeight(): number {
    if (this.values.length <= 1) {
      return 0;
    }

    const highestBranchBottom = this.getFirstBarBranchBottom(this.values.length - 1);
    return Math.max(8, highestBranchBottom - this.getTipBottom(0));
  }

  getFirstBarRightStemHeight(index: number): number {
    return Math.max(8, this.getFirstBarBranchBottom(index) - this.getTipBottom(index));
  }

  getFirstBarBubbleBottom(index: number): number {
    return -9;
  }

  getLeftDropHeight(index: number): number {
    if (index <= 0 || index >= this.values.length) {
      return 6;
    }

    return Math.max(6, this.getConnectorBottom() - this.getBarTopBottom(index - 1));
  }

  getRightDropHeight(index: number): number {
    if (index <= 0 || index >= this.values.length) {
      return 6;
    }

    return Math.max(6, this.getConnectorBottom() - this.getBarTopBottom(index));
  }

  getBubbleBottom(index: number): number {
    return this.getPairTopBottom(index) + 4;
  }

  getBubbleOnConnectorBottom(): number {
    return this.getConnectorBottom() - 9;
  }

  protected getByPath(source: any, path: string): any {
    return path.split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }

      if (/^\d+$/.test(key)) {
        return acc[Number(key)];
      }

      return acc[key];
    }, source);
  }

  protected extractArrayComparisons(
    groupData: any[],
    comparisonBaseData: any,
    percentageKey: string,
    labelCount: number
  ): Array<number | null> {
    if (Array.isArray(comparisonBaseData)) {
      if (comparisonBaseData.length === labelCount) {
        return comparisonBaseData.slice(1).map((entry: any) => this.readComparisonValue(entry, percentageKey));
      }

      if (comparisonBaseData.length === labelCount - 1) {
        return comparisonBaseData.map((entry: any) => this.readComparisonValue(entry, percentageKey));
      }
    }

    return groupData
      .slice(1)
      .map((item: any) => this.readComparisonValue(item, percentageKey));
  }

  protected extractObjectComparisons(
    groupData: any,
    comparisonBaseData: any,
    orderedKeys: string[],
    percentageKey: string
  ): Array<number | null> {
    const comparisonSource = (comparisonBaseData && typeof comparisonBaseData === 'object')
      ? comparisonBaseData
      : groupData;

    return orderedKeys
      .slice(1)
      .map((key) => this.readComparisonValue(comparisonSource?.[key], percentageKey));
  }

  protected readComparisonValue(entry: any, percentageKey: string): number | null {
    if (entry === null || entry === undefined) {
      return null;
    }

    if (typeof entry === 'object' && !Array.isArray(entry)) {
      const objectValue = Number(entry?.[percentageKey] ?? entry?.value);
      return Number.isFinite(objectValue) ? objectValue : null;
    }

    const scalarValue = Number(entry);
    return Number.isFinite(scalarValue) ? scalarValue : null;
  }

  protected normalizeComparisons(
    sourceComparisons: any,
    valuesLength: number
  ): Array<number | null> | null {
    if (!Array.isArray(sourceComparisons) || valuesLength <= 1) {
      return null;
    }

    const normalized = sourceComparisons.map((value: any) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? Math.round(numeric) : null;
    });

    if (normalized.length === valuesLength) {
      normalized[0] = null;
      return normalized;
    }

    if (normalized.length === valuesLength - 1) {
      return [null, ...normalized];
    }

    return null;
  }
}
